import { isNil, keys, maxBy } from 'lodash-es';
import { stripeProducts } from '../../../environments/environment';
import {
  type GqlStripePrice,
  type GqlStripeProduct,
  type GqlStripeRecurringInterval,
} from '../../graphql/react-operations';
import { type GqlCompanySubscriptionQuery } from './Subscriptions.graphql';

const recurringIntervals: Record<GqlStripeRecurringInterval, number> = {
  day: 0,
  week: 1,
  month: 2,
  year: 3,
};

// Do not remove legacy products unless it's confirmed nobody is subscribed to them anymore
export const notes = {
  [stripeProducts.shareRegistry]: null,
  [stripeProducts.startup]: null,
  [stripeProducts.grow]: null,
  [stripeProducts.pro]: null,
  [stripeProducts.proButler]: null,
};

export function canUpgradeToProduct(toProduct: GqlStripeProduct, subscribedProduct: { id: string } | null): boolean {
  if (isNil(subscribedProduct)) {
    return true;
  }
  if (toProduct.id === subscribedProduct.id) {
    // It's possible to upgrade within the same product since we want to allow the user to be able to change payment frequency
    return true;
  }
  const ks = keys(toProduct.metadata).filter(k => k.startsWith('upgradableFrom'));
  if (ks.length === 0) {
    return false;
  }
  return ks.some(k => toProduct.metadata[k] === subscribedProduct.id);
}

export function getCurrentPrice(currentSub: GqlCompanySubscriptionQuery['stripeCompanySubscription']) {
  if (isNil(currentSub)) {
    return null;
  }
  const relevantPrices = currentSub.items.data.filter(p => p.price.type === 'recurring');
  return maxBy(relevantPrices, i => i.price.unit_amount || 0)?.price;
}

export function canUpgradeTo(
  to: { price: GqlStripePrice; product: GqlStripeProduct },
  currentSub: GqlCompanySubscriptionQuery['stripeCompanySubscription'],
) {
  if (isNil(currentSub)) {
    return true;
  }
  const currentPrice = getCurrentPrice(currentSub);
  if (isNil(currentPrice)) {
    return true;
  }
  return canUpgradeToPrice(to, { product: { id: currentPrice.product }, price: currentPrice });
}

export function canUpgradeToPrice(
  to: { price: GqlStripePrice; product: GqlStripeProduct },
  subscribed: { price: GqlStripePrice; product: { id: string } },
): boolean {
  if (isNil(subscribed) || isNil(subscribed.price) || isNil(subscribed.product)) {
    return true;
  }
  if (to.price.product !== to.product.id) {
    return false;
  }
  if (subscribed.price.product !== subscribed.product.id) {
    return false;
  }
  if (to.price.id === subscribed.price.id) {
    return false;
  }
  if (!canUpgradeToProduct(to.product, subscribed.product)) {
    return false;
  }
  if (isNil(subscribed.price.recurring)) {
    throw new Error('`recurring` is missing on price');
  }
  if (isNil(to.price.recurring)) {
    throw new Error('`recurring` is missing on price');
  }
  const subInterval = recurringIntervals[subscribed.price.recurring.interval];
  const currInterval = recurringIntervals[to.price.recurring.interval];
  return currInterval >= subInterval;
}
